/**
 * frontend/src/tabs/DiscoverTab.jsx
 * SoundDAO Sprint 6A — Discover tab with Browse + Invite subtabs
 *
 * Layout (Sprint 6A polish): one 560px column (.discover-col).
 *   1. DigHero — Dig the Archive leads: band search + Founder's Picks / Live Music / Netlabels
 *   2. "Artists on SoundDAO" heading (+ creator count)
 *   3. Browse / Invite segmented control (left) with Newest / Top (right) on one row
 *   4. Browse artists — CreatorDiscovery grid (2 columns; 1 below 360px) + invite nudge
 *      Invite your fav — InviteArtistPanel
 *
 * Props:
 *   session — Supabase auth session
 *   user    — users row (for InviteArtistPanel userId)
 */

import React, { useState, useRef } from 'react';
import CreatorDiscovery from '../pages/CreatorDiscovery';
import InviteArtistPanel from '../components/InviteArtistPanel';
import Chatbot from '../Chatbot';
import DigHero from '../components/DigHero';
import './DiscoverTab.css';
import '../styles/archive.css';

export default function DiscoverTab({ session, user }) {
  const [activeSubtab, setActiveSubtab] = useState('browse');
  const [total, setTotal] = useState(null);   // creators on SoundDAO (from CreatorDiscovery)
  const chatRef = useRef(null);
  const userId  = session?.user?.id || user?.id;

  const handleDraftDM = (dmPrompt) => {
    if (chatRef.current) {
      chatRef.current.openWithMessage(dmPrompt);
    } else {
      localStorage.setItem('sounddao_pending_chat_message', dmPrompt);
    }
  };

  // Lighter Browse / Invite segmented control. On Browse it sits in CreatorDiscovery's sort row
  // (Newest / Top on the right); on Invite it stands alone.
  const toggle = (
    <div className="discover-subtab-row" role="tablist" aria-label="Artists on SoundDAO">
      <button
        role="tab"
        aria-selected={activeSubtab === 'browse'}
        className={`discover-subtab ${activeSubtab === 'browse' ? 'discover-subtab--active' : ''}`}
        onClick={() => setActiveSubtab('browse')}
      >
        Browse artists
      </button>
      <button
        role="tab"
        aria-selected={activeSubtab === 'invite'}
        className={`discover-subtab ${activeSubtab === 'invite' ? 'discover-subtab--active' : ''}`}
        onClick={() => setActiveSubtab('invite')}
      >
        Invite your fav
      </button>
    </div>
  );

  return (
    <div className="discover-tab">
      <div className="discover-col">
        {/* Dig the Archive leads the tab (Sprint 6A polish) */}
        <DigHero />

        <h2 className="discover-heading">
          Artists on SoundDAO
          {total !== null && <span className="discover-heading-count">{total.toLocaleString()} creator{total === 1 ? '' : 's'}</span>}
        </h2>

        {/* Browse subtab */}
        {activeSubtab === 'browse' && (
          <>
            <CreatorDiscovery session={session} controlsStart={toggle} onTotal={setTotal} />
            {/* Nudge to invite */}
            <div className="discover-nudge">
              <p className="discover-nudge-text">Don't see your artist?</p>
              <button
                className="discover-nudge-btn"
                onClick={() => setActiveSubtab('invite')}
              >
                Invite them to Big Creative →
              </button>
            </div>
          </>
        )}

        {/* Invite subtab */}
        {activeSubtab === 'invite' && (
          <>
            <div className="discover-controls-row">{toggle}</div>
            {userId && (
              <InviteArtistPanel
                userId={userId}
                onDraftDM={handleDraftDM}
              />
            )}
          </>
        )}
      </div>

      {/* Chatbot FAB for Draft DM */}
      <Chatbot ref={chatRef} user={user} session={session} />
    </div>
  );
}
